// =============================================================================
// src/data/manaus-municipalities.ts — Municipios do AM no DATA core (Tela 01).
//
// Proveniencia: assets/unified-app.js DATA.munNames / DATA.munPop /
//   DATA.urbanIds / DATA.profileParties / DATA.munLayerPalettes (linhas 98-147).
//   Portado verbatim. Chaves sao codigos IBGE de 7 digitos; munPop em milhares
//   [habitantes, eleitores]. Nem todo id tem munPop — getMunPop tem fallback.
// =============================================================================

import type {
  MunicipalityNameMap,
  MunicipalityPopMap,
  MunicipalityId,
  ProfilePartiesByYear,
  MunLayerPalettesMap,
} from '../app/types';

/** Codigo IBGE -> nome de municipio (DATA.munNames). */
export const MANAUS_MUN_NAMES: MunicipalityNameMap = {
  '1300029': 'Alvarães', '1300060': 'Amaturá', '1300086': 'Anamã', '1300102': 'Anori',
  '1300144': 'Apuí', '1300201': 'Atalaia do Norte', '1300300': 'Autazes', '1300409': 'Barcelos',
  '1300508': 'Barreirinha', '1300607': 'Benjamin Constant', '1300631': 'Beruri',
  '1300680': 'Boa Vista do Ramos', '1300706': 'Boca do Acre', '1300805': 'Borba',
  '1300839': 'Caapiranga', '1300904': 'Canutama', '1301001': 'Carauari', '1301100': 'Careiro',
  '1301159': 'Careiro da Várzea', '1301209': 'Coari', '1301308': 'Codajás', '1301407': 'Eirunepé',
  '1301506': 'Envira', '1301605': 'Fonte Boa', '1301654': 'Guajará', '1301704': 'Humaitá',
  '1301803': 'Ipixuna', '1301852': 'Iranduba', '1301902': 'Itacoatiara', '1301951': 'Itamarati',
  '1302009': 'Itapiranga', '1302108': 'Japurá', '1302207': 'Juruá', '1302306': 'Jutaí',
  '1302405': 'Lábrea', '1302504': 'Manacapuru', '1302553': 'Manaquiri', '1302603': 'Manaus',
  '1302702': 'Manicoré', '1302801': 'Maraã', '1302900': 'Maués', '1303007': 'Nhamundá',
  '1303106': 'Nova Olinda do Norte', '1303205': 'Novo Airão', '1303304': 'Novo Aripuanã',
  '1303403': 'Parintins', '1303502': 'Pauini', '1303536': 'Presidente Figueiredo',
  '1303569': 'Rio Preto da Eva', '1303601': 'Santa Isabel do Rio Negro',
  '1303700': 'Santo Antônio do Içá', '1304005': 'Silves', '1303809': 'São Gabriel da Cachoeira',
  '1303908': 'São Paulo de Olivença', '1303957': 'São Sebastião do Uatumã',
  '1304062': 'Tabatinga', '1304104': 'Tapauá', '1304203': 'Tefé', '1304237': 'Tonantins',
  '1304260': 'Uarini', '1304302': 'Urucará', '1304401': 'Urucurituba',
};

/** Populacao bruta de municipio em milhares: [hab, eleitores] (DATA.munPop). */
export const MANAUS_MUN_POP: MunicipalityPopMap = {
  '1302603': [2200, 1320], '1303403': [115, 69], '1301902': [101, 61], '1302504': [98, 59],
  '1301209': [85, 51], '1304062': [67, 40], '1302900': [62, 37], '1304203': [62, 37],
  '1301704': [57, 34], '1302702': [54, 32], '1300706': [33, 20], '1300805': [40, 24],
  '1300607': [42, 25], '1303809': [45, 27], '1303908': [38, 23], '1300409': [28, 17],
  '1302405': [46, 28], '1301001': [29, 17], '1301407': [35, 21], '1301605': [18, 11],
  '1303700': [24, 14], '1300508': [33, 20], '1303536': [35, 21], '1301852': [48, 29],
  '1303569': [36, 22], '1303205': [19, 11], '1300300': [42, 25], '1301100': [38, 23],
  '1300144': [22, 13],
};

/** Ids de municipios tratados como perfil "urban" por DATA.getMunData (DATA.urbanIds). */
export const MANAUS_URBAN_IDS: readonly MunicipalityId[] = [
  '1303403', '1301902', '1302504', '1301209', '1304062', '1302900', '1304203',
  '1301704', '1302702', '1301852', '1303536', '1303569',
];

/** Perfil eleitoral mock por ano -> {capital, urban, rural, alt} (DATA.profileParties). */
export const MANAUS_PROFILE_PARTIES: ProfilePartiesByYear = {
  '2014': {
    capital: ['MDB', '#639922', 'MDB (48%)', 'PT (31%)'],
    urban: ['PT', '#E24B4A', 'PT (42%)', 'MDB (30%)'],
    rural: ['MDB', '#639922', 'MDB (45%)', 'PT (28%)'],
    alt: ['PSDB', '#7F77DD', 'PSDB (39%)', 'MDB (32%)'],
  },
  '2016': {
    capital: ['MDB', '#639922', 'MDB (44%)', 'PT (26%)'],
    urban: ['MDB', '#639922', 'MDB (38%)', 'PSD (29%)'],
    rural: ['MDB', '#639922', 'MDB (42%)', 'PT (25%)'],
    alt: ['PSD', '#378ADD', 'PSD (37%)', 'MDB (30%)'],
  },
  '2018': {
    capital: ['Avante', '#1D9E75', 'Avante (46%)', 'MDB (28%)'],
    urban: ['PSD', '#378ADD', 'PSD (39%)', 'MDB (27%)'],
    rural: ['MDB', '#639922', 'MDB (40%)', 'PSD (26%)'],
    alt: ['PL', '#185FA5', 'PL (36%)', 'MDB (31%)'],
  },
  '2020': {
    capital: ['Avante', '#1D9E75', 'Avante (52%)', 'PL (24%)'],
    urban: ['Avante', '#1D9E75', 'Avante (41%)', 'MDB (27%)'],
    rural: ['MDB', '#639922', 'MDB (38%)', 'Avante (29%)'],
    alt: ['PSD', '#378ADD', 'PSD (36%)', 'Avante (28%)'],
  },
  '2022': {
    capital: ['Avante', '#1D9E75', 'Avante (54%)', 'PL (30%)'],
    urban: ['PL', '#185FA5', 'PL (43%)', 'Avante (28%)'],
    rural: ['PL', '#185FA5', 'PL (39%)', 'MDB (27%)'],
    alt: ['MDB', '#639922', 'MDB (37%)', 'PL (30%)'],
  },
  '2024': {
    capital: ['Avante', '#1D9E75', 'Avante (56%)', 'PL (32%)'],
    urban: ['Avante', '#1D9E75', 'Avante (44%)', 'PL (31%)'],
    rural: ['PL', '#185FA5', 'PL (41%)', 'Avante (28%)'],
    alt: ['MDB', '#639922', 'MDB (38%)', 'PL (29%)'],
  },
};

/** Rampa de cores ordenada por camada para nivel municipal (DATA.munLayerPalettes). */
export const MANAUS_MUN_LAYER_PALETTES: MunLayerPalettesMap = {
  'heatmap-pct': ['#FAEEDA', '#FAC775', '#EF9F27', '#BA7517', '#854F0B'],
  vulnerabilidade: ['#FCEBEB', '#F7C1C1', '#F09595', '#E24B4A', '#A32D2D'],
  pertencimento: ['#C0DD97', '#97C459', '#639922', '#3B6D11'],
};
